import type { CasesAnalyticsSummary } from '@compass/client/types/cases_analytics';
import { CaseReportType, CaseSlaStatus } from '@compass/client/types/cases_analytics';
import React from 'react';

import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SupportedTimeZone } from '@/constants/date';
import { AnalyticsFilters } from '@/pages/analytics/components/AnalyticsFilters';
import { formatInt } from '@/pages/analytics/utils/formatters';

interface CasesFiltersProps {
  programOptions: { label: string; value: string }[];
  selectedProgramIds: string[];
  onProgramFilterChange: (next: string[]) => void;
  reportTypes: CaseReportType[];
  onReportTypesChange: (next: CaseReportType[]) => void;
  slaStatus: CaseSlaStatus;
  onSlaStatusChange: (next: CaseSlaStatus) => void;
  /** Gives the SLA status options their counts. */
  summary?: CasesAnalyticsSummary;
  start: string;
  end: string;
  timeZone: SupportedTimeZone;
  onDateRangeChange: (range: { start: string; end: string }) => void;
}

const REPORT_TYPE_OPTIONS = [
  { label: 'AE', value: String(CaseReportType.CASE_REPORT_TYPE_AE) },
  { label: 'PQC', value: String(CaseReportType.CASE_REPORT_TYPE_PQC) },
];

const withCount = (label: string, count: number | undefined): string =>
  count == null ? label : `${label} (${formatInt(count)})`;

export const CasesFilters: React.FC<CasesFiltersProps> = ({
  reportTypes,
  onReportTypesChange,
  slaStatus,
  onSlaStatusChange,
  summary,
  ...analyticsFilterProps
}) => {
  const slaOptions = [
    {
      value: CaseSlaStatus.CASE_SLA_STATUS_UNSPECIFIED,
      label: withCount('All Cases', summary?.submitted_cases?.value),
    },
    {
      value: CaseSlaStatus.CASE_SLA_STATUS_WITHIN,
      label: withCount('Within SLA', summary?.within_sla_cases?.value),
    },
    {
      value: CaseSlaStatus.CASE_SLA_STATUS_BREACHED,
      label: withCount('SLA Breached', summary?.breached_cases?.value),
    },
  ];

  return (
    <AnalyticsFilters
      {...analyticsFilterProps}
      extraFilters={
        <>
          <SearchableMultiSelect
            options={REPORT_TYPE_OPTIONS}
            selected={reportTypes.map(String)}
            onChange={next => onReportTypesChange(next.map(Number) as CaseReportType[])}
            placeholder="All Report Types"
            className="w-44 px-3"
            contentClassName="w-auto min-w-[180px]"
            searchPlaceholder="Search report types..."
            emptyMessage="No report types found."
          />
          <Select
            value={String(slaStatus)}
            onValueChange={value => onSlaStatusChange(Number(value) as CaseSlaStatus)}
          >
            <SelectTrigger className="h-9 w-48" aria-label="SLA status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {slaOptions.map(option => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      }
    />
  );
};
